import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { colors, containerVariants, itemVariants } from '../../styled/StyledComponents';
import { CONTACT_EMAIL_ADVISORY } from '../../../config/links';
import biocharImage from '../../../images/biochar.png';

const SectionRoot = styled(Box)({
  backgroundColor: '#FFFFFF',
  padding: '140px 0',
  position: 'relative',
});

const Overline = styled(Typography)({
  color: colors.primary,
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '0.3em',
  textTransform: 'uppercase',
  marginBottom: 16,
});

const Headline = styled(Typography)({
  color: colors.text,
  fontSize: 'clamp(2rem, 3.5vw, 3rem)',
  fontWeight: 700,
  lineHeight: 1.15,
  marginBottom: 24,
  letterSpacing: '-0.01em',
});

const Body = styled(Typography)({
  color: colors.textLight,
  fontSize: '1.05rem',
  lineHeight: 1.7,
  marginBottom: 20,
});

const BiocharImage = styled('img')({
  width: '100%',
  height: 'auto',
  borderRadius: 12,
  boxShadow: '0 20px 50px rgba(0,0,0,0.12)',
  display: 'block',
});

const StatCard = styled(motion.div)({
  backgroundColor: '#F7F8F7',
  borderLeft: `3px solid ${colors.primary}`,
  padding: '24px 20px',
  borderRadius: 4,
  height: '100%',
});

const StatValue = styled(Typography)({
  color: colors.primary,
  fontSize: '1.5rem',
  fontWeight: 700,
  lineHeight: 1.1,
  marginBottom: 6,
});

const StatLabel = styled(Typography)({
  color: colors.textLight,
  fontSize: '0.88rem',
  lineHeight: 1.45,
});

const CtaLink = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  color: colors.primary,
  fontSize: '1rem',
  fontWeight: 600,
  textDecoration: 'none',
  borderBottom: `2px solid ${colors.primary}`,
  paddingBottom: 4,
  transition: 'gap 0.25s ease',
  '&:hover': {
    gap: 14,
  },
  '.MuiSvgIcon-root': {
    fontSize: '1rem',
  },
});

const ChartWrapper = styled(Box)({
  marginTop: 96,
  maxWidth: 700,
  marginLeft: 'auto',
  marginRight: 'auto',
  textAlign: 'center',
});

const ChartTitle = styled(Typography)({
  color: colors.text,
  fontSize: '1rem',
  fontWeight: 700,
  marginBottom: 24,
  letterSpacing: '0.02em',
});

const ChartFootnote = styled(Typography)({
  color: colors.textLight,
  fontSize: '0.8rem',
  fontStyle: 'italic',
  marginTop: 16,
});

const BiocharSection = () => {
  const { t } = useTranslation();

  const statKeys = ['plots', 'rates', 'fertilizer', 'crop'];

  const chartData = [
    { key: 'control', value: 0 },
    { key: 'low_std', value: 1.2 },
    { key: 'low_red', value: 1.1 },
    { key: 'high_std', value: 1.8 },
    { key: 'high_red', value: 1.6 },
  ].map((d) => ({
    ...d,
    name: t(`landing.biochar.chart.conditions.${d.key}`),
  }));

  return (
    <SectionRoot id="biochar-section">
      <Container maxWidth="xl">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={containerVariants}
        >
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center" sx={{ mb: 10 }}>
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Overline>{t('landing.biochar.overline')}</Overline>
                <Headline>{t('landing.biochar.title')}</Headline>
                <Body>{t('landing.biochar.body1')}</Body>
                <Body sx={{ mb: 4 }}>{t('landing.biochar.body2')}</Body>
                <CtaLink href={`mailto:${CONTACT_EMAIL_ADVISORY}`}>
                  {t('landing.biochar.cta')}
                  <ArrowForwardIcon />
                </CtaLink>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <BiocharImage src={biocharImage} alt="Biochar field trial" />
              </motion.div>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {statKeys.map((key) => (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <StatCard variants={itemVariants}>
                  <StatValue>{t(`landing.biochar.stats.${key}.value`)}</StatValue>
                  <StatLabel>{t(`landing.biochar.stats.${key}.label`)}</StatLabel>
                </StatCard>
              </Grid>
            ))}
          </Grid>

          <motion.div variants={itemVariants}>
            <ChartWrapper>
              <ChartTitle>{t('landing.biochar.chart.title')}</ChartTitle>
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 16, right: 16, left: 0, bottom: 24 }}
                  >
                    <XAxis
                      dataKey="name"
                      axisLine={{ stroke: '#CFCFCF' }}
                      tickLine={false}
                      interval={0}
                      tick={{ fill: colors.textLight, fontSize: 11 }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: colors.textLight, fontSize: 11 }}
                      label={{
                        value: t('landing.biochar.chart.yAxis'),
                        angle: -90,
                        position: 'insideLeft',
                        offset: 14,
                        style: {
                          fill: colors.textLight,
                          fontSize: 11,
                          textAnchor: 'middle',
                        },
                      }}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(12, 132, 102, 0.06)' }}
                      contentStyle={{
                        border: `1px solid ${colors.primary}`,
                        borderRadius: 4,
                        fontSize: 12,
                      }}
                      formatter={(value) => [value, t('landing.biochar.chart.yAxis')]}
                    />
                    <Bar
                      dataKey="value"
                      fill={colors.primary}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={56}
                    >
                      <LabelList
                        dataKey="value"
                        position="top"
                        style={{ fill: colors.text, fontSize: 11, fontWeight: 600 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              <ChartFootnote>{t('landing.biochar.chart.footnote')}</ChartFootnote>
            </ChartWrapper>
          </motion.div>
        </motion.div>
      </Container>
    </SectionRoot>
  );
};

export default BiocharSection;
